import { AlertColor, useMediaQuery, useTheme } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import strings from '../../../../common/Translation/Translate';
import Notification from '../../../../components/Notification';
import { FormDataModel } from '../../../../components/Form';
import axiosInstance from '../../../../utils/axios';
import { PageProps } from '../../../../utils/constants/data';
import { privileges } from '../../../../utils/constants/privileges';
import { URLS } from '../../../../utils/constants/urls';
import { languages } from '../../../../utils/redux/reducer/authentication-slice';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import { getListData } from '../../../../utils/validators/HelperFunctions';
import ListPage from '../../CommonPage';
import MasterDataEntityDrawer from '../../CommonPage/MasterDataEntityDrawer';
import { lastUpdatedTableColumn } from '../../CommonPage/lastUpdatedColumn';
import { MailTemplateForm } from './AddEditMailTemplate/MailTemplateForm';

const MailTemplateList: FC = () => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const drawerWidth = screenSizeDownSm ? '100%' : screenSizeDownMd ? '85%' : '55%';
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const languageList = useSelector(languages) as { text: string; value: string }[];

  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormDataModel>({});
  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const formModel = useMemo(() => {
    const [subjectField, templateField] = MailTemplateForm(strings);
    return (languageList || []).flatMap(language => {
      const subject = { ...subjectField };
      subject.field = `subject_${language.value}`;
      subject.label = `${subject.label} (${language.text})`;
      subject.responsive = { xs: 12 };

      const template = { ...templateField };
      template.field = `template_${language.value}`;
      template.label = `${template.label} (${language.text})`;
      template.responsive = { xs: 12 };
      template.minRows = 6;

      return [subject, template];
    });
  }, [languageList]);

  const getList = useCallback(
    (filterData: PageProps) => {
      const body = filterData
        ? filterData
        : { page: index + 1, size: pageSize, filter: filter ?? [], sort: ['createdAt:desc'] };
      getListData(setLoading, body, URLS.mailTemplate, setPageResponse);
    },
    [index, pageSize, filter],
  );

  useEffect(() => {
    getList(null as any);
  }, [getList]);

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error?.response?.data?.error_description ?? error?.response?.data?.message ?? error?.message,
    });
  };

  const openDrawer = useCallback(
    async (recordId: number) => {
      setEditingId(recordId);
      try {
        setDrawerLoading(true);
        const { data } = await axiosInstance.get(URLS.get_edit_delete_mailTemplateById(recordId));
        const container: FormDataModel = {};
        (languageList || []).forEach(language => {
          const localized = data?.localizedAttributes?.[language.value];
          container[`subject_${language.value}`] = localized?.subject ?? '';
          container[`template_${language.value}`] = localized?.template ?? '';
        });
        setFormValues(container);
      } catch (error: any) {
        handleCatch(error);
        return;
      } finally {
        setDrawerLoading(false);
      }
      setDrawerOpen(true);
    },
    [languageList],
  );

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setFormValues({});
  };

  const handleSave = async (body: FormDataModel) => {
    if (!editingId) return;
    try {
      setSaving(true);
      const localizedAttributes = (languageList || []).map(item => ({
        language: item.value,
        subject: body[`subject_${item.value}`],
        template: body[`template_${item.value}`],
      }));
      await axiosInstance.put(URLS.get_edit_delete_mailTemplateById(editingId), { localizedAttributes });
      setMessage({ display: true, severity: 'success', message: strings.common_update_message });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const pageConfig = useMemo(
    () => ({
      title: strings.mailTemplate_title_list,
      subtitle: strings.mailTemplate_list_subtitle,
      searchField: 'type',
      searchPlaceholder: 'Search mail templates',
      AddButtonText: '',
      listApi: URLS.mailTemplate,
      deleteApi: null,
      editPrivilege: privileges.edit_mail_template,
      addPrivilege: null,
      customEdit: (id: number) => openDrawer(id),
      tableColumn: [
        { field: 'type', headerName: strings.mailTemplate_column_type, flex: 5, sortable: true },
        lastUpdatedTableColumn(),
      ],
    }),
    [openDrawer],
  );

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <ListPage
        isLoading={loading}
        isWhiteSpace={false}
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        getList={getList}
      />
      <MasterDataEntityDrawer
        open={drawerOpen}
        title={strings.mailTemplate_title_edit}
        isEdit={Boolean(editingId)}
        formModel={formModel}
        formValues={formValues}
        loading={drawerLoading}
        saving={saving}
        canDelete={false}
        width={drawerWidth}
        onClose={closeDrawer}
        onSave={handleSave}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}
      />
    </>
  );
};
export default MailTemplateList;
